import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs/Observable';
import { AppConfigService } from '../../services/app-config.service';

/**
 * Centralise la gestion de la session applicative :
 * - détection de l'état de connexion côté client (cookies applicatifs non `HttpOnly`),
 * - rafraîchissement du token d'accès (`ACCESS_TOKEN`) via le `REFRESH_TOKEN` (cookie `HttpOnly`),
 * - déconnexion serveur (invalidation des cookies JWT).
 *
 * Les cookies `ACCESS_TOKEN` / `REFRESH_TOKEN` sont `HttpOnly` et ne sont donc jamais lus,
 * écrits ou supprimés ici : ils sont uniquement gérés par le backend via `Set-Cookie`.
 */
@Injectable()
export class AuthTokenService {

  /**
   * Paths sur lesquels des cookies applicatifs ont pu historiquement être posés (le path par
   * défaut d'un cookie dépend de l'URL courante au moment du `set()` si aucun path explicite
   * n'est fourni, ex : '/home' depuis la page d'accueil, '/home' depuis '/home/cerfa'...). On
   * supprime sur tous les paths connus pour garantir qu'aucun cookie résiduel ne subsiste et ne
   * fasse croire à une session active, ou à une session d'un autre utilisateur (fail-closed).
   */
  private static readonly LEGACY_COOKIE_PATHS = ['/', '/home', '/notaire'];

  constructor(
    private readonly http: HttpClient,
    private readonly cookieService: CookieService
  ) { }

  /**
   * Indique si un utilisateur semble connecté, d'après la présence du cookie applicatif `given_name`.
   * Ceci est un indicateur d'UI : la validité réelle du token `ACCESS_TOKEN` est vérifiée par le
   * backend à chaque appel `rest/*` (une session expirée aboutira à un `401` géré par
   * `AuthRefreshInterceptor`).
   */
  isConnected(): boolean {
    return !!this.cookieService.get('given_name');
  }

  /**
   * Demande le renouvellement du token d'accès grâce au `REFRESH_TOKEN` (cookie `HttpOnly`
   * envoyé automatiquement par le navigateur, aucun corps de requête n'est nécessaire).
   */
  refresh(): Observable<void> {
    const url = `${AppConfigService.settings.urlServeur}rest/auth/refresh/`;
    return this.http.post<void>(url, {});
  }

  /**
   * Invalide la session côté serveur : les cookies `ACCESS_TOKEN` / `REFRESH_TOKEN` sont
   * purgés/expirés par le backend.
   */
  logout(): Observable<void> {
    const url = `${AppConfigService.settings.urlServeur}rest/auth/logout/`;
    return this.http.post<void>(url, {});
  }

  /**
   * Supprime TOUS les cookies applicatifs locaux (`sub`, `given_name`, `idCerfa`, `email`,
   * `legacyLogin`, `logout_url`, mais aussi les cookies techniques FranceConnect : state, nonce,
   * authorization code/token...) sur tous les paths connus. N'a aucun effet sur les cookies
   * `HttpOnly` `ACCESS_TOKEN` / `REFRESH_TOKEN`, qui doivent être invalidés côté serveur via
   * `logout()`.
   *
   * Point d'entrée UNIQUE de déconnexion locale : à utiliser partout où une session doit être
   * invalidée côté client (échec de refresh, échec d'authentification FranceConnect, déconnexion
   * volontaire), afin qu'aucun cookie résiduel d'un utilisateur précédent ne subsiste (ex : nom
   * d'un autre utilisateur affiché après reconnexion avec un compte différent).
   */
  clearLocalSession(): void {
    AuthTokenService.LEGACY_COOKIE_PATHS.forEach(path => this.cookieService.deleteAll(path));
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/auth-token.service.ts